package com.evoapps.evoshift.settings

import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.util.APP_LANGUAGES
import com.evoapps.evoshift.util.appLanguage
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.launch

/**
 * Keeps the signed-in person's profile (users/{uid}) in step with the app language (SH-137,
 * SH-138):
 * - the first time this device sees the person (sign-in, app start), a language they picked on
 *   another device is taken over here; if they picked none there but did here (e.g. before
 *   signing in), this one is saved;
 * - afterwards, whenever the language changes, `language` (the language the app shows: picked,
 *   else the device's) and `languagePreference` (picked, or null) are saved. The functions write
 *   e-mails, pushes and roster PDFs in `language`.
 * Only the person's own fields; nothing is written when the profile already says the same.
 */
class ProfileLanguageSync(
    private val getCurrentUser: GetCurrentUserUseCase,
    private val users: UserRepository,
    private val settings: AppLanguageSettings,
    /** The language the app shows now (picked, else the device's). */
    private val effectiveLanguage: () -> String = ::appLanguage
) {
    /** The person this device last took the profile's pick over for (kept across restarts of [start]). */
    private var seenUid: String? = null

    fun start(scope: CoroutineScope): Job = scope.launch {
        combine(getCurrentUser(), settings.preference) { user, preference -> user to preference }
            .collect { (user, preference) ->
                if (user == null) {
                    seenUid = null
                    return@collect
                }
                if (seenUid != user.uid) {
                    seenUid = user.uid
                    val picked = user.languagePreference?.takeIf { it in APP_LANGUAGES }
                    if (picked != null && picked != preference) {
                        // Switches the app; the change comes back here and is then saved.
                        settings.choose(picked)
                        return@collect
                    }
                }
                val language = effectiveLanguage()
                if (user.language != language || user.languagePreference != preference) {
                    users.updateLanguage(user.uid, language, preference)
                }
            }
    }
}
